// ECO opening-name lookup (plan Item C1). Names any position from the vendored
// EPD→"<eco>\t<name>" table (`src/data/openings.json`, generated by
// `scripts/build-openings.mjs`). The table is lazily imported so its ~430 KB never
// weighs on the play/analysis path — only boards that ask for an opening pull it.

import type { Review } from "./review.ts";

export interface OpeningHit {
  eco: string;
  name: string;
  ply: number; // half-move index of the matched position (deepest wins)
}

type Table = Record<string, string>;

const MAX_PLY = 30; // openings live in the first ~30 plies; cap the walk for perf

let cache: Table | null = null;
let loading: Promise<Table> | null = null;

/** Lazily load (and memoise) the opening table. */
async function loadTable(): Promise<Table> {
  if (cache) return cache;
  if (!loading) {
    loading = import("../data/openings.json").then((m) => {
      cache = ((m as { default?: Table }).default ?? (m as unknown)) as Table;
      return cache;
    });
    // A transient failure (ad-blocker, network hiccup on the ~430KB chunk) must
    // not permanently disable opening lookups: clear the memoised promise on
    // rejection so a later call can retry instead of re-awaiting a dead promise.
    loading.catch(() => {
      loading = null;
    });
  }
  return loading;
}

/** First four FEN fields = the position's EPD (no move counters). */
function epdOf(fen: string): string {
  return fen.split(" ").slice(0, 4).join(" ");
}

/** Deepest opening match against a preloaded table (pure — testable offline). */
export function findOpening(review: Review, table: Table): OpeningHit | null {
  let hit: OpeningHit | null = null;
  const cap = Math.min(review.nodes.length, MAX_PLY + 1); // include node 0..MAX_PLY
  for (let i = 0; i < cap; i++) {
    const v = table[epdOf(review.nodes[i].fen)];
    if (v) {
      const tab = v.indexOf("\t");
      hit = { eco: v.slice(0, tab), name: v.slice(tab + 1), ply: i }; // deepest wins
    }
  }
  return hit;
}

/** Name the opening of a review, or null. Loads the table on first use; a load
 *  failure degrades to `null` (no opening name) rather than rejecting, and
 *  `loadTable` will retry the import on the next call. */
export async function lookupOpening(review: Review): Promise<OpeningHit | null> {
  try {
    const table = await loadTable();
    return findOpening(review, table);
  } catch {
    return null;
  }
}
